/**
 * Transition registry — route-pair resolution with dynamic imports.
 *
 * Maps source:destination namespace pairs to transition modules.
 * Resolves in priority order: exact → *:to → from:* → *:*
 * Transition modules are loaded on demand to keep the critical path small.
 *
 * @see specs/002-swup-view-transitions/contracts/router-api.md
 */

/**
 * Route-pair → transition module path map.
 *
 * Keys use "from:to" format where namespaces come from body[data-namespace].
 * Wildcard (*) matches any namespace.
 *
 * Add new entries here to assign transitions to specific route pairs.
 */
const transitionMap = {
    'work:projects/show': 'flip-expand',
    'home:projects/show': 'flip-expand',
    'work:*':             'stagger',
    '*:home':             'reveal',
    '*:*':                'crossfade',
};

/**
 * Module cache — avoids re-importing the same transition module.
 */
const moduleCache = new Map();

/**
 * Dynamically import a transition module by name.
 * Modules live in resources/js/transitions/{name}.js
 *
 * @param {string} name - Transition module name (e.g. 'crossfade', 'slide')
 * @returns {Promise<{out: Function, in: Function}>}
 */
function loadTransition(name) {
    if (moduleCache.has(name)) {
        return moduleCache.get(name);
    }

    const promise = import(`../transitions/${name}.js`);
    moduleCache.set(name, promise);
    return promise;
}

/**
 * Resolve the transition name for a visit based on route-pair matching.
 *
 * Resolution order:
 * 1. Exact match: "from:to"
 * 2. Wildcard destination: "*:to"
 * 3. Wildcard source: "from:*"
 * 4. Default: "*:*" (always crossfade)
 *
 * @param {string} from - Source namespace
 * @param {string} to - Destination namespace
 * @returns {string} Transition name
 */
function resolveTransitionName(from, to) {
    const exact = `${from}:${to}`;
    if (transitionMap[exact]) return transitionMap[exact];

    const wildcardTo = `*:${to}`;
    if (transitionMap[wildcardTo]) return transitionMap[wildcardTo];

    const wildcardFrom = `${from}:*`;
    if (transitionMap[wildcardFrom]) return transitionMap[wildcardFrom];

    return transitionMap['*:*'];
}

/**
 * Resolve the transition for a Swup visit.
 *
 * Sets visit.animation.name to the resolved transition name,
 * then returns the dynamically imported transition module.
 * Used as the JsPlugin transitions handler.
 *
 * @param {Object} visit - Swup visit object
 * @returns {Promise<{out: Function, in: Function}>}
 */
export function resolveTransition(visit) {
    // Both `from` and `to` are derived from the URL. body[data-namespace] would
    // be ideal, but Statamic doesn't populate `{{ template }}` for collection
    // entries that inherit their template from the collection config (e.g.
    // /work/{slug}), so that attribute is unreliable here.
    const from = inferToNamespace(visit.from?.url || location.pathname) || '*';
    const to = inferToNamespace(visit.to?.url) || '*';

    // Trigger-based override: any link carrying `data-flip-id` opts into the
    // card → hero FLIP transition regardless of the registry match.
    const hasFlipTrigger = !!visit.trigger?.el?.closest?.('[data-flip-id]');

    // Back-button collapse: popstate nav from a project page back to any
    // page that may contain a matching flip target (the carousel on /home,
    // the featured-work block on /home or /work, etc.). Limited to popstate
    // so forward navigation stays on its registry-resolved transition.
    const isPopstate =
        visit.history?.popstate === true || !visit.trigger?.el;
    const isReverseFlip =
        isPopstate && (from === 'projects/show' || from === 'employees/show');

    let name;
    if (hasFlipTrigger) {
        name = 'flip-expand';
    } else if (isReverseFlip) {
        name = 'flip-collapse';
    } else {
        name = resolveTransitionName(from, to);
    }

    visit.animation.name = name;

    return loadTransition(name);
}

/**
 * Infer the destination `data-namespace` from a URL. The template name on each
 * page is also its namespace (layout.antlers.html sets body[data-namespace] to
 * {{ template }}), so we map URL patterns to template names here.
 */
function inferToNamespace(url) {
    if (!url) return null;
    const path = url.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
    if (path === '/' || path === '') return 'home';
    if (path === '/work') return 'work';
    if (/^\/work\/[^/]+$/.test(path)) return 'projects/show';
    if (/^\/employees\/[^/]+$/.test(path)) return 'employees/show';
    if (path === '/contact') return 'contact';
    // Fallback: strip leading slash as a best-effort namespace guess.
    return path.replace(/^\//, '').replace(/\//g, '/');
}
